import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  Output,
  PLATFORM_ID,
  SimpleChanges
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CmsService, ScriptLoader } from '@spartacus/core';
import { Router } from '@angular/router';
import { Subscription, take } from 'rxjs';
import { v4 as uuid } from 'uuid';

import { BaseStoreService, ConfigurationService } from '../../../services';
import { SdTitleComponent } from '@seb/web-design-system/core';
import {
  CustomBaseStore,
  RatingAndReviewsWidget,
  ShoppableContentInterface,
  SkeeperShoppableContentInterface
} from '../../../models';
import { pageTypes, ScriptPositionEnum, SectionEnum, WidgetTypes } from '../../../enums';
import { mapPageTemplateToPageType, waitForElm, waitForShadowRoot } from '../../../utils';

@Component({
  selector: 'wc-skeeper-shoppable-content',
  templateUrl: './skeeper-shoppable-content.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdTitleComponent
  ]
})
export class SkeeperShoppableContentComponent implements OnChanges {
  @Input()
  data: SkeeperShoppableContentInterface;

  @Input()
  hideNavigationControls = true;

  @Output()
  hasVideos = new EventEmitter<boolean>();

  @Input() class = '';
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.hideCmp ? 'is-hidden' : ''
    ].filter(Boolean).join(' ');
  }

  hideCmp = true;
  subscriptions: Subscription;
  uuid: string = uuid();

  private readonly baseStore: CustomBaseStore;

  constructor(
    private readonly cmsService: CmsService,
    private readonly router: Router,
    private readonly scriptLoader: ScriptLoader,
    private readonly cdRef: ChangeDetectorRef,
    private readonly baseStoreService: BaseStoreService,
    private readonly configService: ConfigurationService,
    @Inject(PLATFORM_ID) private readonly platformId: any
  ) {
    this.baseStore = this.baseStoreService.baseStore;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.data.currentValue?.product && changes.data.currentValue?.product !== changes.data.previousValue?.product) ||
      changes.data.currentValue?.collection && changes.data.currentValue?.collection !== changes.data.previousValue?.collection) {
      if (isPlatformBrowser(this.platformId) && (this.data?.product || this.data?.collection)) {
        this.initShoppableWidgetSkeepers(this.data);
      }
    }
  }

  private initShoppableWidgetSkeepers(data: ShoppableContentInterface) {
    this.cmsService.getCurrentPage()
      .pipe(take(1))
      .subscribe((page) => {
        const currentPageType = mapPageTemplateToPageType(page, this.router.parseUrl(this.router.url));
        const shoppableScript = this.findShoppableScript(currentPageType);

        if (shoppableScript?.content) {
          this.loadShoppableScript(shoppableScript.content, data);
        } else {
          this.hasVideos.emit(false);
        }
      });
  }

  private findShoppableScript(currentPageType: pageTypes): RatingAndReviewsWidget | undefined {
    if (currentPageType !== pageTypes.NGT) {
      return this.baseStore.ratingAndReviewsWidgets?.find((reviewWidget) => {
        return reviewWidget.widgetType === WidgetTypes.SCRIPT &&
          reviewWidget.pagesTypes?.includes(currentPageType) &&
          reviewWidget.position?.includes(ScriptPositionEnum.PDP_BODY) &&
          reviewWidget.section?.includes(SectionEnum.SHOPPABLE_CONTENT);
      });
    } else {
      const globalScript = this.configService.config?.script?.url?.shoppableContent;
      return globalScript ? { content: globalScript } : undefined;
    }
  }

  private loadShoppableScript(script: string, data: ShoppableContentInterface) {
    if (this.scriptLoader.hasScript(script)) {
      this.activateShoppableWidget(data);
    } else {
      this.scriptLoader.embedScript({
        src: script,
        attributes: { id: 'skeepers-shoppable-script' },
        callback: () => this.activateShoppableWidget(data)
      });
    }
  }

  private activateShoppableWidget(data: ShoppableContentInterface) {
    waitForElm(`#ShoppableContainer-${this.uuid}`).then((result: HTMLElement) => {
      const previousWidgets = result.querySelectorAll('skp-widget');
      const widget = document.createElement('skp-widget');
      if (previousWidgets?.length > 0) {
        this.hideCmp = true;
        previousWidgets.forEach((skpWidget) => skpWidget.remove());
      }
      widget.setAttribute('identifier', this.baseStore.shoppableContentAccountId);
      widget.setAttribute('add-to-cart', 'false');
      widget.setAttribute('link-to-product-page', 'false');
      if (data?.collection) {
        widget.setAttribute('collection', data.collection);
      } else if (data?.product) {
        widget.setAttribute('product', data.product);
      }
      result.appendChild(widget);

      waitForElm('skp-widget').then((result: HTMLElement) => {
        waitForShadowRoot(result).then((shadowRoot: ShadowRoot) => {
          const observer = new MutationObserver(() => {
            this.hideCmp = false;
            const skpWidget = shadowRoot.querySelector('.skp-widget');
            if (skpWidget) {
              observer.disconnect();
              const videos = skpWidget.querySelectorAll('.skp-slider-wrapper li');
              const _hasVideos = videos.length > 0;
              if (this.hideNavigationControls) {
                const skpSliderNavigations = skpWidget.querySelectorAll('.skp-slider-navigation');
                skpSliderNavigations.forEach((skpSliderNavigation) => skpSliderNavigation.remove());
              } else {
                const styles = document.createElement('style');
                styles.textContent =
                  '  .skp-slider-navigation__controls {\n' +
                  '    border: 0.1rem solid var(--skp-slider-nav-icon-color);\n' +
                  '  }'
                ;
                skpWidget.appendChild(styles);
              }
              this.hideCmp = !_hasVideos;
              this.hasVideos.emit(_hasVideos);
              this.cdRef.detectChanges();
            }
          });

          observer.observe(shadowRoot, {
            childList: true,
            subtree: true
          });
        });
      });
    });
  }
}
